import type { ReactNode } from 'react';
import React, { useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useSearch } from './contexts/SearchContext';
import SearchTag from './SearchTag';
import { ReactComponent as SearchIcon } from '~/assets/search.svg';

type SearchInputProps = {
    children?: ReactNode;
};

export const SearchInput: React.FC<SearchInputProps> = ({ children }) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const {
        editingKey,
        editingSearchField,
        handleClose,
        handleSearchFields,
        handleTagDelete,
        lastTagHighlighted,
        searchFields,
        searchQuery,
        setFlyoutOpen,
        setLastTagHighlighted,
        setSearchQuery,
    } = useSearch();

    const onFocus = () => {
        setFlyoutOpen(true);
        if (!editingSearchField) {
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                setFlyoutOpen(true);
                if (!editingSearchField) {
                    inputRef.current?.focus();
                }
                break;
            case 'Escape':
                handleClose();
                break;
            case 'Tab':
                e.preventDefault();
                handleSearchFields(inputRef.current);
                break;
            case 'Backspace':
                if (
                    (!searchQuery || searchQuery.length === 0) &&
                    Object.keys(searchFields).length > 0 &&
                    e.target instanceof HTMLElement &&
                    e.target.textContent === ''
                ) {
                    e.preventDefault();

                    if (!lastTagHighlighted) {
                        setLastTagHighlighted(true);
                    } else {
                        const keysArray = Object.keys(searchFields);
                        const lastKey = keysArray[keysArray.length - 1];
                        handleTagDelete(lastKey);
                        setLastTagHighlighted(false);
                    }
                }
                break;
            default:
                break;
        }
    };

    return (
        <div
            data-testid="search-click-area"
            className="relative flex items-center p-0 mr-1 bg-transparent flex-grow z-[100] rounded border border-grey-dark"
            onClick={onFocus}
        >
            <SearchIcon />
            <div
                data-testid="search-input-wrapper"
                className="flex flex-wrap items-center min-h-[30px] bg-white w-full cursor-text text-xs leading-4 pl-8 rounded"
                onFocus={onFocus}
            >
                {Object.keys(searchFields).length > 0 &&
                    Object.keys(searchFields).map((key) => {
                        const isLastTag = key === Object.keys(searchFields)[Object.keys(searchFields).length - 1];
                        const value = searchFields[key];
                        const isEditing = editingSearchField && key === editingKey;

                        return (
                            <SearchTag
                                key={key}
                                fieldKey={key}
                                value={value}
                                isEditing={isEditing}
                                isLastTag={isLastTag}
                                inputRef={inputRef}
                            />
                        );
                    })}
                <input
                    type="text"
                    ref={inputRef}
                    autoComplete="off"
                    data-testid="search-input"
                    className="inline-block text-gray-600 min-w-[1em] outline-none flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                    placeholder={Object.keys(searchFields).length === 0 ? t('LEADS.SEARCH.PLACEHOLDER') : ''}
                    onInput={(e) => {
                        setSearchQuery((e.target as HTMLInputElement).value || null);
                        if (lastTagHighlighted) {
                            setLastTagHighlighted(false);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                />
            </div>
            {children}
        </div>
    );
};
